import React from "react";
import { CircleRounded } from "@mui/icons-material";
import { formatKey } from "../../utils/generalUtils.js";

// This component is used to render dynamic content based on the type of data passed to it.
export const CompanyDetailItem = ({ icon, value }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  // Format value as comma-separated string if it's an array
  const formattedValue = Array.isArray(value) ? value.join(", ") : value;

  return (
    <div className="flex items-start gap-2 max-w-full">
      <div className="flex-shrink-0 p-2 bg-gray-200 rounded-lg">{icon}</div>
      <div className="text-gray-500 break-words whitespace-pre-wrap overflow-hidden">
        {formattedValue}
      </div>
    </div>
  );
};

// This function takes a value and an optional array of links, and renders the content accordingly.
// It handles strings, arrays, and objects, and formats them for display.
// It also supports rendering hyperlinks for URLs and special cases like <|n|> replacements.
export const RenderDynamicContent = (value, links = []) => {
  if (typeof value === "string") {
    // If the string is a direct URL, return it as a clickable link
    if (value.startsWith("http")) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline break-all"
        >
          {value}
        </a>
      );
    }

    // Handle <|n|> replacement by linking to corresponding `links[n]`
    return value.split(/<\|(\d+)\|>/g).map((part, index) => {
      if (/^\d+$/.test(part)) {
        const linkIndex = parseInt(part, 10);
        const link = links[linkIndex];
        return link ? (
          <a
            key={index}
            href={link.uri}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline mx-1"
          >
            [{linkIndex}]
          </a>
        ) : (
          `[${part}]`
        );
      }
      return <span key={index}>{part}</span>;
    });
  }

  if (Array.isArray(value)) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2">
        {value.map((item, index) => (
          <div key={index} className="flex items-center space-x-2 break-words">
            <CircleRounded
              sx={{ fontSize: "10px" }}
              className="text-gray-500"
            />{" "}
            {/* Icon Before Text */}
            <span>{RenderDynamicContent(item, links)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "object" && value !== null) {
    // ✅ Special case: If the object has "uri" and "title", render a hyperlink with the title
    if ("uri" in value && typeof value.uri === "string") {
      return (
        <a
          href={value.uri}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline break-all"
        >
          {value.title || value.uri}
        </a>
      );
    }

    return (
      <div>
        {Object.entries(value).map(([key, nestedValue]) => {
          return (
            <div key={key} className="mt-1">
              <h4 className="font-semibold text-gray-700 capitalize">
                {formatKey(key)}
              </h4>
              <div className="text-gray-800">
                {RenderDynamicContent(nestedValue, links)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};
